'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';

interface TodoFormProps {
  onSubmit: (data: {
    title: string;
    details?: string;
    due_date?: string;
    delegate?: string;
    notes?: string;
    sensitive?: boolean;
    critical?: boolean;
  }) => void;
  onCancel?: () => void;
  onDone?: () => void;
  compact?: boolean;
  initialData?: {
    title: string;
    details?: string | null;
    due_date?: string | null;
    delegate?: string | null;
    notes?: string | null;
    sensitive?: boolean;
    critical?: boolean;
  };
  todoId?: string | null;
  decisionId?: string | null;
  readOnly?: boolean;
  showAssist?: boolean;
  showManagementFields?: boolean;
}

export default function TodoForm({
  onSubmit,
  onCancel,
  onDone,
  compact = false,
  initialData,
  todoId,
  decisionId,
  readOnly = false,
  showAssist = false,
  showManagementFields = true,
}: TodoFormProps) {
  const [title, setTitle] = useState(initialData?.title || '');
  const [details, setDetails] = useState(initialData?.details || '');
  const [dueDate, setDueDate] = useState(initialData?.due_date || '');
  const [delegate, setDelegate] = useState(initialData?.delegate || '');
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [sensitive, setSensitive] = useState(initialData?.sensitive || false);
  const [critical, setCritical] = useState(initialData?.critical || false);
  const isEditing = !!initialData;
  const hasNotesData = Boolean(initialData?.notes);
  const [showNotes, setShowNotes] = useState(hasNotesData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle Escape key to cancel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onCancel) {
        e.preventDefault();
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        details: details.trim() || undefined,
        due_date: dueDate || undefined,
        delegate: delegate.trim() || undefined,
        notes: notes.trim() || undefined,
        sensitive,
        critical,
      });
      // Only reset form when adding new (not editing)
      if (!initialData) {
        setTitle('');
        setDetails('');
        setDueDate('');
        setDelegate('');
        setNotes('');
        setShowNotes(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (compact) {
    return (
      <form onSubmit={handleSubmit} className="space-y-2">
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="What needs to be done?"
          className="w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg
                     bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                     focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                     placeholder-zinc-400 dark:placeholder-zinc-500"
          autoFocus
        />
        <div className="flex gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400
                         dark:hover:text-zinc-200"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={!title.trim() || isSubmitting}
            className="flex-1 px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600
                       dark:bg-blue-400 dark:text-zinc-900 dark:hover:bg-blue-500
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Add
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="What needs to be done?"
          disabled={readOnly}
          className={`w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 rounded-lg
                     bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                     focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                     placeholder-zinc-400 dark:placeholder-zinc-500
                     ${readOnly ? 'cursor-default' : ''}`}
          autoFocus={!readOnly}
        />
      </div>

      {/* Details field - always visible */}
      <div>
        <textarea
          value={details}
          onChange={e => setDetails(e.target.value)}
          placeholder="Details (optional)"
          rows={2}
          disabled={readOnly}
          className={`w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg
                     bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                     focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                     placeholder-zinc-400 dark:placeholder-zinc-500
                     ${readOnly ? 'cursor-default' : ''}`}
        />
      </div>

      {/* Due date and Delegate fields - hidden for delegates */}
      {showManagementFields && (
        <div className="flex gap-3">
          <div className="flex-1">
            {(dueDate || !readOnly) && (
              <>
                <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">
                  Due date
                </label>
                {readOnly ? (
                  <p className="px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100">
                    {dueDate ? new Date(dueDate + 'T12:00:00').toLocaleDateString() : '—'}
                  </p>
                ) : dueDate ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={dueDate}
                      onChange={e => setDueDate(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg
                                 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                                 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setDueDate('')}
                      className="p-2 text-zinc-400 hover:text-red-500"
                      aria-label="Clear date"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      setDueDate(today);
                    }}
                    className="w-full px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400 border border-dashed
                               border-zinc-300 dark:border-zinc-700 rounded-lg hover:border-zinc-400
                               dark:hover:border-zinc-600 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
                  >
                    + Add due date
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex-1">
            {(delegate || !readOnly) && (
              <>
                <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">
                  Delegate to
                </label>
                {readOnly ? (
                  <p className="px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100">
                    {delegate
                      ? delegate.split(/[,\s]+/).filter(Boolean).map(d => `@${d.replace(/^@/, '')}`).join(', ')
                      : '—'}
                  </p>
                ) : (
                  <input
                    type="text"
                    value={delegate}
                    onChange={e => setDelegate(e.target.value)}
                    placeholder="@user1, @user2"
                    className="w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg
                               bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                               focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                               placeholder-zinc-400 dark:placeholder-zinc-500"
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Notes section - toggle to show/hide */}
      {showNotes ? (
        <div>
          <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">
            Notes
          </label>
          {readOnly ? (
            <p className="px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 whitespace-pre-wrap">
              {notes || '—'}
            </p>
          ) : (
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Progress updates, blockers, or additional context..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg
                         bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                         focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                         placeholder-zinc-400 dark:placeholder-zinc-500"
            />
          )}
        </div>
      ) : (
        !readOnly && (
          <button
            type="button"
            onClick={() => setShowNotes(true)}
            className="text-xs text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            + Add notes
          </button>
        )
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {decisionId && (
            <span className="text-xs">
              <span className="text-zinc-500 dark:text-zinc-400">Linked to: </span>
              <a
                href={`/decisions/${decisionId}`}
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
              >
                View decision →
              </a>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <>
              {isEditing && showAssist && todoId && (
                <Link
                  href={`/todos/assist/${todoId}`}
                  className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Assist
                </Link>
              )}
              {onDone && (
                <button
                  type="button"
                  onClick={onDone}
                  className="inline-flex items-center gap-1 px-2 py-1 text-sm text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-600 mr-8"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Done
                </button>
              )}
              {showManagementFields && (
                <>
                  <button
                    type="button"
                    onClick={() => setCritical(!critical)}
                    className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors mr-2 ${
                      critical
                        ? 'bg-amber-600 text-white hover:bg-amber-700'
                        : 'border border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                    }`}
                  >
                    {critical ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    )}
                    {critical ? 'Critical' : 'Mark Critical'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSensitive(!sensitive)}
                    className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors mr-8 ${
                      sensitive
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                    }`}
                  >
                    {sensitive ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                      </svg>
                    )}
                    {sensitive ? 'Sensitive' : 'Mark Sensitive'}
                  </button>
                </>
              )}
            </>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400
                         dark:hover:text-zinc-200"
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
          )}
          {!readOnly && (
            <button
              type="submit"
              disabled={!title.trim() || isSubmitting}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:text-zinc-900 dark:hover:bg-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (isEditing ? 'Updating...' : 'Adding...') : isEditing ? 'Update' : 'Add todo'}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
